const ARRAY_KEYS = [
  "items",
  "records",
  "results",
  "data",
  "content",
  "ahdSubscriptionPlans",
  "subscriptionPlans",
  "plans",
  "coupons",
  "services",
  "allServiceCategories",
];

const OBJECT_KEYS = [
  "item",
  "record",
  "data",
  "subscriptionPlan",
  "plan",
  "coupon",
  "service",
];

const NESTED_OBJECT_KEYS = ["data", "result", "payload", "content", "meta"];
const SUCCESS_STATUS = "SUCCESS";

export const getValueByPath = (value, path) => {
  if (!path) {
    return value;
  }

  return path.split(".").reduce((current, key) => current?.[key], value);
};

export const unwrapPayload = (payload) => {
  let current = payload;

  while (
    current &&
    typeof current === "object" &&
    !Array.isArray(current) &&
    typeof current.data === "object" &&
    current.data !== null &&
    !Array.isArray(current.data)
  ) {
    current = current.data;
  }

  return current;
};

export const getResponseMessage = (payload, fallbackMessage = "") => {
  const normalizedPayload = unwrapPayload(payload);

  return (
    normalizedPayload?.text ||
    normalizedPayload?.message ||
    normalizedPayload?.error ||
    fallbackMessage
  );
};

export const isSuccessfulResponse = (payload) => {
  const normalizedPayload = unwrapPayload(payload);

  if (!normalizedPayload || typeof normalizedPayload !== "object") {
    return true;
  }

  if (!normalizedPayload.statusType) {
    return true;
  }

  return String(normalizedPayload.statusType).toUpperCase() === SUCCESS_STATUS;
};

export const assertSuccessfulResponse = (
  payload,
  fallbackMessage = "Unable to load records."
) => {
  if (!isSuccessfulResponse(payload)) {
    throw new Error(getResponseMessage(payload, fallbackMessage));
  }

  return unwrapPayload(payload);
};

export const extractCollection = (payload, preferredKeys = []) => {
  const normalizedPayload = unwrapPayload(payload);
  const keys = [...preferredKeys, ...ARRAY_KEYS];

  for (const key of keys) {
    const candidate = normalizedPayload?.[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  if (Array.isArray(normalizedPayload)) {
    return normalizedPayload;
  }

  for (const nestedKey of NESTED_OBJECT_KEYS) {
    const nestedValue = normalizedPayload?.[nestedKey];
    if (nestedValue && typeof nestedValue === "object") {
      const nestedCollection = extractCollection(nestedValue, preferredKeys);
      if (nestedCollection.length) {
        return nestedCollection;
      }
    }
  }

  return [];
};

export const extractEntity = (payload, preferredKeys = []) => {
  const normalizedPayload = unwrapPayload(payload);
  const keys = [...preferredKeys, ...OBJECT_KEYS];

  for (const key of keys) {
    const candidate = normalizedPayload?.[key];
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      return candidate;
    }
  }

  for (const nestedKey of NESTED_OBJECT_KEYS) {
    const nestedValue = normalizedPayload?.[nestedKey];
    if (nestedValue && typeof nestedValue === "object" && !Array.isArray(nestedValue)) {
      const nestedEntity = extractEntity(nestedValue, preferredKeys);
      if (nestedEntity) {
        return nestedEntity;
      }
    }
  }

  if (
    normalizedPayload &&
    typeof normalizedPayload === "object" &&
    !Array.isArray(normalizedPayload)
  ) {
    return normalizedPayload;
  }

  return null;
};

export const buildCollectionResult = ({
  payload,
  preferredKeys = [],
  pageSize = 10,
  normalizeItem = (item) => item,
  fallbackMessage = "Unable to load records.",
  getTotalPages,
}) => {
  const normalizedPayload = assertSuccessfulResponse(payload, fallbackMessage);
  const items = extractCollection(normalizedPayload, preferredKeys).map(normalizeItem);

  return {
    items,
    totalPages: getTotalPages(normalizedPayload, items.length, pageSize),
    raw: normalizedPayload,
  };
};

export const matchesSearch = (record, searchTerm, fields = []) => {
  if (!searchTerm) {
    return true;
  }

  const normalizedSearch = String(searchTerm).trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return fields.some((field) => {
    const value = getValueByPath(record, field);

    if (value === null || value === undefined) {
      return false;
    }

    return String(value).toLowerCase().includes(normalizedSearch);
  });
};
